import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NavDot } from "./NavDots";

const isOn = (n, path) => (n.end ? path === n.to : path === n.to || path.startsWith(`${n.to}/`));

const Badge = ({ n, color, testid }) => (
  <span data-testid={testid} className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold grid place-items-center ${color}`}>{n}</span>
);

const Item = ({ item, dots, counts, onNav, nested }) => (
  <NavLink to={item.to} end={item.end} onClick={onNav} data-testid={`nav-${item.to.replace("/", "") || "home"}`}
    className={({ isActive }) => `flex items-center gap-3 ${nested ? "pl-9 pr-3 py-2" : "px-3 py-2.5"} rounded-md text-sm font-semibold transition-colors duration-150 ${isActive ? "bg-sky-50 text-sky-700 shadow-[inset_2px_0_0_0_#0284c7]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
    {!nested && <item.icon className="h-4 w-4" />}
    {item.label}
    {item.dot && dots[item.dot] && <NavDot k={item.dot} />}
    {item.badge && counts[item.badge] > 0 && <Badge n={counts[item.badge]} color={item.badge === "quotes" ? "bg-sky-600" : "bg-rose-600"} testid={`nav-${item.badge}-badge`} />}
  </NavLink>
);

// Sidebar with collapsible groups, one group open at a time.
export function SideNav({ items, groups, path, dots, counts, onNav }) {
  const activeGroup = items.find((n) => n.group && isOn(n, path))?.group || null;
  const [open, setOpen] = useState(activeGroup);
  useEffect(() => { if (activeGroup) setOpen(activeGroup); }, [activeGroup]);
  const seen = new Set();
  return items.map((item) => {
    const kids = item.group ? items.filter((n) => n.group === item.group) : [];
    if (!item.group || kids.length < 2) return <Item key={item.to} item={item} dots={dots} counts={counts} onNav={onNav} />;
    if (seen.has(item.group)) return null;
    seen.add(item.group);
    const g = groups[item.group];
    const isOpen = open === item.group;
    const total = kids.reduce((s, k) => s + (k.badge ? counts[k.badge] || 0 : 0), 0);
    const anyDot = kids.some((k) => k.dot && dots[k.dot]);
    return (
      <div key={`g-${item.group}`} data-testid={`nav-group-${item.group}`}>
        <button type="button" onClick={() => setOpen(isOpen ? null : item.group)} data-testid={`nav-group-${item.group}-toggle`} aria-expanded={isOpen}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors duration-150 ${activeGroup === item.group ? "nav-group-active text-sky-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
          <g.icon className="h-4 w-4" />
          {g.label}
          {!isOpen && anyDot && <NavDot k={`group-${item.group}`} />}
          {!isOpen && total > 0 && <Badge n={total} color="bg-rose-600" testid={`nav-group-${item.group}-badge`} />}
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${total > 0 && !isOpen ? "" : "ml-auto"} ${isOpen ? "rotate-180" : ""}`} />
        </button>
        <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
          <div className="overflow-hidden">
            <div className="relative mt-0.5 mb-1 space-y-0.5 before:absolute before:left-[1.35rem] before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
              {kids.map((k, i) => (
                <div key={k.to} className={`transition-[transform,opacity] duration-300 ${isOpen ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"}`} style={{ transitionDelay: isOpen ? `${60 + i * 40}ms` : "0ms" }}>
                  <Item item={k} dots={dots} counts={counts} onNav={onNav} nested />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  });
}
